/**
 * Trame d'entretien Pré-recrutement Shariti — fil conducteur unique.
 *
 * Cet objet est :
 *   1. La source de vérité pour le SEED initial de la collection MongoDB
 *      `entretien_script` (cf. backend/services/seed.py).
 *   2. Le FALLBACK utilisé par la scorecard si l'appel à
 *      `/api/entretien/script` échoue (network down, etc.).
 *
 * Chaque item est une question numérotée Q1...QN dans la scorecard,
 * dans l'ordre du déroulé d'appel idéal :
 *   1. Vérification éligibilité (gating)
 *   2. Découverte & motivation
 *   3. Test mental & résilience
 *   4. Projection
 *   5. Observation recruteur
 *
 * `kind` :
 *  - `yesno_gating` : oui/non, éliminatoire si "non"
 *  - `rating`       : note 1-5 (score live)
 *  - `rating_gating`: note 1-5 + éliminatoire si la note ≤ 2
 *  - `text`         : champ libre, pas de score
 *  - `yesno_text`   : oui/non + précision
 *
 * Quand `criterion_ids` est renseigné, la note 1-5 alimente le scoring
 * pondéré (référentiel Shariti V2 — backend/models/scorecard_criteria.py).
 *
 * `with_comment: true` force l'affichage du champ commentaire même
 * pour un yes/no, pour pouvoir noter la précision donnée par le candidat.
 */
export const ENTRETIEN_SCRIPT = [
    // ─────────────────────────────────────────── 1. VÉRIFICATION ÉLIGIBILITÉ
    {
        id: "in_search",
        phase: "verification",
        q: "Êtes-vous toujours en recherche d'emploi actuellement ?",
        kind: "yesno_gating",
    },
    {
        id: "availability",
        phase: "verification",
        q: "Êtes-vous disponible immédiatement pour travailler ? À partir de quand ?",
        kind: "rating_gating",
        criterion_ids: ["disponibilite"],
        criterion_hint: "1 = pas disponible / 3 = avec contraintes / 5 = immédiate. ≤ 2 = éliminatoire.",
    },
    {
        id: "geo_mobility",
        phase: "verification",
        q: "Êtes-vous bien mobile géographiquement sur la zone de l'offre ?",
        kind: "yesno_gating",
        hint: "Adapter : Île-de-France si offre Paris, etc. Si « Non » → éliminatoire.",
        with_comment: true,
    },
    {
        id: "format_24h",
        phase: "verification",
        q: "Notre format chez Shariti c'est 24 h par semaine, du lundi au samedi, sur les créneaux 10 h-14 h ou 16 h-20 h. Est-ce que ce format vous convient ?",
        kind: "yesno_gating",
        with_comment: true,
        hint: "Si « Non » → préciser ce qui bloque (créneaux, jours, durée…).",
    },

    // ─────────────────────────────────────────── 2. DÉCOUVERTE & MOTIVATION
    {
        id: "self_presentation",
        phase: "discovery",
        q: "Présentez-vous en quelques minutes : qui êtes-vous, quel est votre parcours ?",
        kind: "rating",
        criterion_ids: ["energie_vocale", "fluidite_orale"],
        criterion_hint: "Voix vivante, sourire audible, articulation, structure du discours.",
    },
    {
        id: "motivation_role",
        phase: "discovery",
        q: "Pourquoi ce métier de recruteur de donateurs vous attire-t-il, et qu'avez-vous compris du rôle d'ambassadeur terrain ?",
        kind: "rating",
        criterion_ids: ["motivation"],
        criterion_hint: "Motivation claire (sens, challenge, humain) > uniquement financier. Vérifier que le candidat a bien compris la nature du poste (rue, magasin, donateurs réguliers).",
    },
    {
        id: "approach",
        phase: "discovery",
        q: "Concrètement, comment aborderiez-vous une personne inconnue dans la rue ou en magasin ?",
        kind: "rating",
        criterion_ids: ["bagout"],
        criterion_hint: "Facilité à engager, naturel, présence sociale.",
    },
    {
        id: "knows_charity",
        phase: "discovery",
        q: "Connaissez-vous l'association pour laquelle vous postulez ? Que pouvez-vous m'en dire ?",
        kind: "rating",
        criterion_ids: ["sensibilite_cause"],
        criterion_hint: "Authenticité de l'intérêt associatif, connaissance des causes.",
    },

    // ─────────────────────────────────────────── 3. TEST MENTAL & RÉSILIENCE
    {
        id: "adaptability",
        phase: "mental",
        q: "Êtes-vous à l'aise avec des lieux ou des équipes qui changent ?",
        kind: "rating",
        criterion_ids: ["mobilite"],
        criterion_hint: "Souplesse, curiosité, capacité à s'adapter à un nouveau cadre. > Rigidité, refus de contraintes terrain.",
    },
    {
        id: "resilience_story",
        phase: "mental",
        q: "Parlez-moi d'une situation difficile que vous avez réussi à tenir malgré tout.",
        kind: "rating",
        criterion_ids: ["resilience"],
        criterion_hint: "Capacité à tenir, exemples concrets > posture victime.",
    },
    {
        id: "handle_rejection",
        phase: "mental",
        q: "Comment réagissez-vous face à un refus ou à une personne désagréable dans la rue ?",
        kind: "rating",
        criterion_ids: ["bienveillance"],
        criterion_hint: "Respect, calme, empathie > agressivité, mépris.",
    },
    {
        id: "coachability",
        phase: "mental",
        q: "Comment réagissez-vous quand on vous corrige ou qu'on vous fait un retour critique ?",
        kind: "rating",
        criterion_ids: ["coachabilite"],
        criterion_hint: "Écoute, envie d'apprendre > se justifie, ego fort.",
    },
    {
        id: "maturity",
        phase: "mental",
        q: "Qu'est-ce qui pourrait vous faire abandonner rapidement un poste ?",
        kind: "rating",
        criterion_ids: ["maturite"],
        criterion_hint: "Lucidité, responsabilité, recul.",
    },

    // ─────────────────────────────────────────── 4. PROJECTION
    {
        id: "duration_wish",
        phase: "projection",
        q: "Pour combien de temps souhaiteriez-vous travailler chez Shariti si on vous embauche ?",
        kind: "text",
    },
    {
        id: "understands_pay",
        phase: "projection",
        q: "Qu'avez-vous compris de la rémunération que nous proposons ?",
        kind: "text",
    },
    {
        id: "punctual",
        phase: "projection",
        q: "Vous définiriez-vous comme une personne assidue et ponctuelle ? Donnez-moi un exemple si possible.",
        kind: "yesno_text",
    },

    // ─────────────────────────────────────────── 5. OBSERVATION RECRUTEUR
    {
        id: "reactivity_observation",
        phase: "observation",
        q: "Observation recruteur — vitesse de réaction aux messages/appels avant l'entretien.",
        kind: "rating",
        criterion_ids: ["reactivite"],
        criterion_hint: "Basé sur les échanges précédents : réactivité, confirmation des créneaux.",
        no_speak: true, // ne pas poser à voix haute
    },
];

export const PHASES = {
    verification: { label: "Vérification éligibilité", emoji_safe: "1" },
    discovery: { label: "Découverte & motivation", emoji_safe: "2" },
    mental: { label: "Test mental & résilience", emoji_safe: "3" },
    projection: { label: "Projection", emoji_safe: "4" },
    observation: { label: "Observation recruteur", emoji_safe: "5" },
};
